import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useDarkMode } from '../../contexts/DarkModeContext';
import loggingService from '../../services/loggingService';
import { stalkerAPI, getStaticFileUrl, getProxyUrl } from '../../services/api';
import { Instagram, Twitter, Music, User, Camera, Video, MessageCircle, Calendar, Play, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Log {
	sl_id: number;
	sl_platform: string;
	sl_changeType: string;
	sl_description: string;
	sl_oldValue?: any;
	sl_newValue?: any;
	created_at: string;
	accountDetail: any | null;
}

interface Media {
	sm_id: number;
	sm_platform: string;
	sm_filePath: string;
	sm_mediaType: string;
	platform_username?: string;
	platform_fullname?: string;
	person_avatar?: string;
	sm_socialId?: number | string;
}

interface Birthday {
	person_id: number;
	person_name: string;
	person_surname: string;
	person_avatar: string;
	person_birthdate: string;
	next_birthday: string;
}

interface DashboardStats {
	importantLogs: Log[];
	latestMedias: Media[];
	nearestBirthdays: Birthday[];
}

const getFieldFromChangeType = (changeType: string) => {
	const t = (changeType || '').trim();
	if (!t.endsWith('_changed')) return null;
	return t.slice(0, -'_changed'.length);
};

const pickValue = (val: any, field: string | null) => {
	if (field && val && typeof val === 'object' && field in val) return val[field];
	return val;
};

const toNumberOrNull = (v: any): number | null => {
	if (v === null || v === undefined) return null;
	const n = typeof v === 'number' ? v : Number(v);
	return Number.isFinite(n) ? n : null;
};

const formatValue = (v: any) => {
	if (v === null || v === undefined) return '-';
	if (typeof v === 'number') return v.toLocaleString('en-US');
	return String(v);
};

const getUsernameFromLog = (log: any) => {
	if (!log.accountDetail) return '';
	if (log.sl_platform === 'instagram') return log.accountDetail.iu_username;
	if (log.sl_platform === 'tiktok') return log.accountDetail.tu_username;
	if (log.sl_platform === 'vsco') return log.accountDetail.vu_username;
	if (log.sl_platform === 'x' || log.sl_platform === 'twitter') return log.accountDetail.xu_username;
	if (log.sl_platform === 'snapchat') return log.accountDetail.su_username;
	if (log.sl_platform === 'spotify') return log.accountDetail.su_username;
	return '';
};

const getSocialIdFromLog = (log: any) => {
	if (!log.accountDetail) return '';
	if (log.sl_platform === 'instagram') return log.accountDetail.iu_id;
	if (log.sl_platform === 'tiktok') return log.accountDetail.tu_id;
	if (log.sl_platform === 'vsco') return log.accountDetail.vu_id;
	if (log.sl_platform === 'x' || log.sl_platform === 'twitter') return log.accountDetail.xu_id;
	if (log.sl_platform === 'snapchat') return log.accountDetail.su_id;
	if (log.sl_platform === 'spotify') return log.accountDetail.su_id;
	return '';
};

const getAvatarFromLog = (log: any) => {
	if (!log.accountDetail) return null;
	if (log.sl_platform === 'instagram') return getProxyUrl(log.accountDetail.iu_profilePicUrl);
	if (log.sl_platform === 'tiktok') return getStaticFileUrl(log.accountDetail.tu_avatarUrl);
	if (log.sl_platform === 'vsco') return getStaticFileUrl(log.accountDetail.vu_avatarUrl);
	return null;
};


const BirthdayCountdown: React.FC<{ daysLeft: number; isDark: boolean }> = ({ daysLeft, isDark }) => {
	const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);

	useEffect(() => {
		if (daysLeft > 1) return;

		const calculateTimeLeft = () => {
			if (daysLeft === 0) {
				return { hours: 0, minutes: 0, seconds: 0 };
			}
			const now = new Date();
			const target = new Date();
			target.setHours(24, 0, 0, 0);
			const diff = target.getTime() - now.getTime();
			const hours = Math.floor(diff / (1000 * 60 * 60));
			const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
			const seconds = Math.floor((diff % (1000 * 60)) / 1000);
			return { hours, minutes, seconds };
		};

		setTimeLeft(calculateTimeLeft());
		const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
		return () => clearInterval(timer);
	}, [daysLeft]);

	if (daysLeft === 0) {
		return <span className="text-[10px] font-mono tracking-wider text-emerald-500 uppercase">ACTIVE TODAY</span>;
	}

	if (daysLeft === 1 && timeLeft) {
		const { hours, minutes, seconds } = timeLeft;
		return (
			<div className={`flex items-center text-[10px] font-mono tracking-wider ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
				<span>{String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}</span>
			</div>
		);
	}

	return <span className={`text-[10px] font-mono uppercase tracking-widest ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`}>{daysLeft} DAYS left</span>;
};

const Dashboard: React.FC = () => {
	const { user } = useAuth();
	const { isDark } = useDarkMode();
	const navigate = useNavigate();
	const hasLoggedPageView = useRef(false);
	const [stats, setStats] = useState<DashboardStats | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!hasLoggedPageView.current) {
			loggingService.logPageView('/dashboard', null, 'Dashboard');
			hasLoggedPageView.current = true;
		}

		const fetchDashboardStats = async () => {
			try {
				const response = await stalkerAPI.dashboard.get();
				setStats(response.data.data);
			} catch (error) {
				console.error('Failed to fetch dashboard stats', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchDashboardStats();
	}, []);

	const getPlatformIcon = (platform: string, className = "w-3 h-3") => {
		switch (platform.toLowerCase()) {
			case 'instagram': return <Instagram className={`${className} ${isDark ? 'text-pink-500' : 'text-pink-600'}`} />;
			case 'vsco': return <Camera className={`${className} ${isDark ? 'text-neutral-300' : 'text-neutral-700'}`} />;
			case 'tiktok': return <Video className={`${className} ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />;
			case 'x':
			case 'twitter': return <Twitter className={`${className} ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />;
			case 'snapchat': return <MessageCircle className={`${className} ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} />;
			case 'spotify': return <Music className={`${className} ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`} />;
			default: return <ExternalLink className={`${className} ${isDark ? 'text-neutral-500' : 'text-neutral-400'}`} />;
		}
	};

	const bgClass = isDark ? 'bg-[#0a0a0a]' : 'bg-neutral-50';
	const panelClass = isDark ? 'bg-[#111] border border-[#222]' : 'bg-white border border-neutral-200';
	const textClass = isDark ? 'text-neutral-300' : 'text-neutral-600';
	const headingClass = isDark ? 'text-white' : 'text-black';
	const mutedTest = isDark ? 'text-neutral-500' : 'text-neutral-400';
	const hoverClass = isDark ? 'hover:bg-[#1a1a1a]' : 'hover:bg-neutral-50';
	const borderClass = isDark ? 'border-[#222]' : 'border-neutral-200';

	return (
		<div className={`w-full min-h-full ${bgClass} font-sans flex flex-col space-y-4`}>

			<div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
				
				{/* LEFT COLUMN: Birthdays & Medias */}
				<div className="xl:col-span-4 space-y-4 flex flex-col">
					
					{/* Birthdays Panel */}
					<div className={`p-0 ${panelClass} rounded-sm flex flex-col`}>
						<div className={`px-4 py-3 border-b ${borderClass} flex items-center justify-between`}>
							<div className="flex items-center gap-2">
								<Calendar className={`w-3.5 h-3.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
								<h2 className={`text-xs font-mono uppercase tracking-wider font-semibold ${headingClass}`}>Target Birthdays</h2>
							</div>
							<span className={`text-[9px] font-mono ${mutedTest}`}>TTL</span>
						</div>
						<div className="flex-1 overflow-y-auto">
							{isLoading ? (
								<p className={`text-[10px] font-mono p-4 ${mutedTest}`}>[ Fetching data... ]</p>
							) : stats?.nearestBirthdays && stats.nearestBirthdays.length > 0 ? (
								<div className="flex flex-col">
									{stats.nearestBirthdays.map((bday, i) => (
										<div
											key={i}
											className={`flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 ${borderClass} ${hoverClass} transition-colors group cursor-pointer`}
											onClick={() => navigate(`/stalker/persons/${bday.person_id}`)}
										>
											<div className="flex items-center gap-3">
												{bday.person_avatar ? (
													<img src={getStaticFileUrl(bday.person_avatar) || ""} alt="AVT" className={`w-8 h-8 rounded-sm object-cover border ${borderClass} group-hover:border-emerald-500/50 transition-colors`} />
												) : (
													<div className={`w-8 h-8 rounded-sm ${isDark ? 'bg-[#1a1a1a] text-neutral-400' : 'bg-neutral-200 text-neutral-500'} flex items-center justify-center font-mono text-[11px] font-bold`}>
														{bday.person_name.charAt(0)}{bday.person_surname.charAt(0)}
													</div>
												)}
												<div className="flex flex-col">
													<p className={`text-[11px] font-medium leading-tight ${headingClass}`}>
														{bday.person_name} {bday.person_surname}
													</p>
													<p className={`text-[9px] font-mono uppercase tracking-widest ${mutedTest}`}>
														{format(new Date(bday.person_birthdate), 'MMM dd')}
													</p>
												</div>
											</div>
											<div className="text-right">
												<BirthdayCountdown daysLeft={Number(bday.next_birthday)} isDark={isDark} />
											</div>
										</div>
									))}
								</div>
							) : (
								<p className={`text-[10px] font-mono p-4 ${mutedTest}`}>[ No target birthdays indexed ]</p>
							)}
						</div>
					</div>

					{/* Recent Medias Panel */}
					<div className={`p-0 ${panelClass} rounded-sm flex flex-col flex-1 min-h-[300px]`}>
						<div className={`px-4 py-3 border-b ${borderClass} flex items-center gap-2`}>
							<Camera className={`w-3.5 h-3.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
							<h2 className={`text-xs font-mono uppercase tracking-wider font-semibold ${headingClass}`}>Intercepted Media</h2>
						</div>
						<div className="p-2 flex-1 overflow-y-auto">
							{isLoading ? (
								<p className={`text-[10px] font-mono p-2 ${mutedTest}`}>[ Fetching data... ]</p>
							) : stats?.latestMedias && stats.latestMedias.length > 0 ? (
								<div className="grid grid-cols-4 sm:grid-cols-5 xl:grid-cols-4 gap-1.5">
									{stats.latestMedias.map((media) => (
										<div key={media.sm_id} className={`relative group overflow-hidden border ${isDark ? 'border-[#222] bg-[#0a0a0a]' : 'border-neutral-200 bg-neutral-100'} cursor-pointer`} style={{aspectRatio: "1 / 1.1"}} onClick={() => navigate(`/stalker/medias`)}>
											{media.sm_mediaType === 'video' ? (
												<div className="w-full h-full relative cursor-pointer">
													<video src={getStaticFileUrl(media.sm_filePath) || ""} className="w-full h-full object-cover" preload="metadata" />
													<div className="absolute inset-0 flex items-center justify-center bg-black/40">
														<Play className="w-4 h-4 text-white" fill="white" />
													</div>
												</div>
											) : media.sm_mediaType === 'audio' ? (
												<div className="w-full h-full flex items-center justify-center relative">
													<Music className={`w-4 h-4 ${mutedTest} absolute z-10`} />
												</div>
											) : (
												<img src={getStaticFileUrl(media.sm_filePath) || ""} alt="Media" className="w-full h-full object-cover" />
											)}
											
											{/* Technical Overlay */}
											<div className="absolute top-0 left-0 right-0 p-1 flex justify-between items-start bg-gradient-to-b from-black/80 to-transparent pointer-events-none">
												<span className="text-[8px] font-mono text-white/90 truncate mr-1">
													{media.platform_username ? `@${media.platform_username}` : ''}
												</span>
												<div className="bg-black/60 p-0.5 rounded-sm flex-shrink-0">
													{getPlatformIcon(media.sm_platform, "w-2.5 h-2.5 text-white")}
												</div>
											</div>
											<div className="absolute bottom-1 right-1 px-1 py-0.5 bg-black/80 text-[8px] font-mono text-emerald-400 border border-emerald-500/30 pointer-events-none">
												ID: {media.sm_id}
											</div>
										</div>
									))}
								</div>
							) : (
								<p className={`text-[10px] font-mono p-2 ${mutedTest}`}>[ No intercepted media found ]</p>
							)}
						</div>
					</div>

				</div>

				{/* RIGHT COLUMN: Important Intel */}
				<div className={`xl:col-span-8 p-0 ${panelClass} rounded-sm flex flex-col`}>
					<div className={`px-4 py-3 border-b ${borderClass} flex items-center justify-between`}>
						<div className="flex items-center gap-2">
							<MessageCircle className={`w-3.5 h-3.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
							<h2 className={`text-xs font-mono uppercase tracking-wider font-semibold ${headingClass}`}>Event Feed / Intelligence</h2>
						</div>
						<div className="flex items-center gap-4">
							<span className={`text-[9px] font-mono ${mutedTest}`}>LIVE STREAM</span>
							<div className="w-1.5 h-1.5 bg-cyan-500 rounded-full animate-pulse"></div>
						</div>
					</div>
					
					{/* Table Header */}
					<div className={`grid grid-cols-[90px_1fr_140px] md:grid-cols-[110px_1.5fr_1fr_140px] gap-2 px-4 py-2 border-b ${borderClass} ${isDark ? 'bg-[#0a0a0a]' : 'bg-neutral-100'}`}>
						<div className={`text-[10px] font-mono uppercase tracking-widest ${mutedTest}`}>Timestamp</div>
						<div className={`text-[10px] font-mono uppercase tracking-widest ${mutedTest}`}>Target / Platform</div>
						<div className={`hidden md:block text-[10px] font-mono uppercase tracking-widest ${mutedTest}`}>Event Type</div>
						<div className={`text-[10px] font-mono uppercase tracking-widest text-right ${mutedTest}`}>Delta / Record</div>
					</div>

					<div className="overflow-x-hidden">
						{isLoading ? (
							<p className={`text-[10px] font-mono p-4 ${mutedTest}`}>[ Indexing event logs... ]</p>
						) : stats?.importantLogs && stats.importantLogs.length > 0 ? (
							<div className="flex flex-col">
								{stats.importantLogs.map((log) => {
									const username = getUsernameFromLog(log);
									const socialId = getSocialIdFromLog(log);
									const timeStr = format(new Date(log.created_at), 'HH:mm:ss');
									const dateStr = format(new Date(log.created_at), 'MM/dd');
									
									// Extractor helpers
									const changeType = (log.sl_changeType || '').toLowerCase();
									const field = getFieldFromChangeType(log.sl_changeType);
									const oldRaw = pickValue(log.sl_oldValue, field);
									const newRaw = pickValue(log.sl_newValue, field);
									
									let title = log.sl_changeType;
									let detailRenderer = null;
									let isCritical = false;

									if (['followercount_changed', 'followingcount_changed', 'mediacount_changed', 'videocount_changed'].includes(changeType)) {
										title = changeType === 'followercount_changed' ? 'FOLLOWER COUNT CHANGED' :
												changeType === 'followingcount_changed' ? 'FOLLOWING COUNT CHANGED' :
												changeType === 'mediacount_changed' ? 'MEDIA COUNT CHANGED' : 'VIDEO COUNT CHANGED';
										
										const oldN = toNumberOrNull(oldRaw);
										const newN = toNumberOrNull(newRaw);
										const isDecrease = oldN !== null && newN !== null ? oldN > newN : false;
										isCritical = true;
										
										const leftColor = isDecrease ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-600');
										const rightColor = isDecrease ? (isDark ? 'text-red-400' : 'text-red-600') : (isDark ? 'text-emerald-400' : 'text-emerald-600');
										
										detailRenderer = (
											<div className="flex items-center justify-end gap-1.5 text-[10px] font-mono">
												<span className={leftColor}>{formatValue(oldN ?? oldRaw)}</span>
												<span className={`${mutedTest}`}>»</span>
												<span className={`${rightColor} font-bold`}>{formatValue(newN ?? newRaw)}</span>
											</div>
										);
									}
									else if (changeType === 'profilepicurl_changed' || changeType === 'avatarurl_changed') {
										title = 'PROFILE PICTURE CHANGED';
										const isInstagram = log.sl_platform.toLowerCase() === 'instagram';
										const oldUrl = typeof oldRaw === 'string' ? (isInstagram ? (getProxyUrl(oldRaw) || oldRaw) : getStaticFileUrl(oldRaw) || oldRaw) : '';
										const newUrl = typeof newRaw === 'string' ? (isInstagram ? (getProxyUrl(newRaw) || newRaw) : getStaticFileUrl(newRaw) || newRaw) : '';
										
										detailRenderer = (
											<div className="flex items-center justify-end gap-1.5">
												{oldUrl ? <img src={oldUrl} alt="old" className="w-12 h-12 rounded-sm object-cover border border-[#333]" /> : <div className="w-12 h-12 rounded-sm border border-[#333] bg-[#1a1a1a]"></div>}
												<span className={`${mutedTest}`}>»</span>
												{newUrl ? <img src={newUrl} alt="new" className="w-12 h-12 rounded-sm object-cover border border-emerald-500" /> : <div className="w-12 h-12 rounded-sm border border-[#333] bg-[#1a1a1a]"></div>}
											</div>
										);
									}
									else if (changeType === 'isprivate_changed') {
										title = 'PRIVACY CHANGED';
										const newLocked = String(newRaw ?? '') === '1';
										const oldLocked = String(oldRaw ?? '') === '1';
										detailRenderer = (
											<div className="flex items-center justify-end gap-1.5 text-[10px] font-mono">
												<span className={oldLocked ? 'text-red-500 opacity-60' : 'text-emerald-500 opacity-60'}>{oldLocked ? 'PRIVATE' : 'PUBLIC'}</span>
												<span className={`${mutedTest}`}>»</span>
												<span className={newLocked ? 'text-red-500 font-bold' : 'text-emerald-500 font-bold'}>{newLocked ? 'PRIVATE' : 'PUBLIC'}</span>
											</div>
										);
									}
									else if (changeType === 'isverified_changed') {
										title = 'VERIFICATION CHANGED';
										const newVerified = String(newRaw ?? '') === '1';
										const oldVerified = String(oldRaw ?? '') === '1';
										detailRenderer = (
											<div className="flex items-center justify-end gap-1.5 text-[10px] font-mono">
												<span className={oldVerified ? 'text-blue-500 opacity-60' : 'text-neutral-500 opacity-60'}>{oldVerified ? 'VERIFIED' : 'UNVERIFIED'}</span>
												<span className={`${mutedTest}`}>»</span>
												<span className={newVerified ? 'text-blue-500 font-bold' : 'text-neutral-500 font-bold'}>{newVerified ? 'VERIFIED' : 'UNVERIFIED'}</span>
											</div>
										);
									}
									else if (changeType.includes('name_changed')) {
										title = 'PROFILE NAME CHANGED';
										detailRenderer = (
											<div className="flex flex-col items-end text-[9px] font-mono max-w-[150px]">
												<span className={`truncate w-full text-right line-through opacity-60 ${mutedTest}`}>{formatValue(oldRaw)}</span>
												<span className={`truncate w-full text-right ${headingClass}`}>{formatValue(newRaw)}</span>
											</div>
										);
									}
									else if (changeType === 'bio_changed') {
										title = 'BIO CHANGED';
										detailRenderer = (
											<div className="flex items-center justify-end text-[9px] font-mono max-w-[150px]">
												<span className={`truncate w-full text-right ${headingClass}`}>{formatValue(newRaw)}</span>
											</div>
										);
									}
									else if (changeType === 'profilelink_changed') {
										title = 'PROFILE LINK CHANGED';
										detailRenderer = (
											<div className="flex items-center justify-end text-[9px] font-mono max-w-[150px]">
												<span className={`truncate w-full text-right ${headingClass}`}>{formatValue(newRaw)}</span>
											</div>
										);
									}

									return (
										<div 
											key={log.sl_id} 
											className={`grid grid-cols-[90px_1fr_140px] md:grid-cols-[110px_1.5fr_1fr_140px] gap-2 px-4 py-2.5 border-b ${borderClass} ${hoverClass} hover:border-l-2 hover:border-l-indigo-500 transition-all items-center group cursor-pointer`}
											onClick={() => {
												if(socialId && log.sl_platform) {
													navigate(`/stalker/socials/${log.sl_platform}/${socialId}`);
												}
											}}
										>
											{/* Time */}
											<div className="flex flex-col">
												<span className={`text-[12px] font-mono ${headingClass}`}>{timeStr}</span>
												<span className={`text-[10px] font-mono opacity-60 ${mutedTest}`}>{dateStr}</span>
											</div>

											{/* Target */}
											<div className="flex items-center gap-2 min-w-0">
												{getAvatarFromLog(log) ? (
													<img src={getAvatarFromLog(log) || ""} alt="A" className={`w-8 h-8 rounded-sm object-cover border flex-shrink-0 ${borderClass}`} />
												) : (
													<div className={`p-1 w-8 h-8 flex items-center justify-center rounded-sm flex-shrink-0 bg-opacity-20 ${isDark ? 'bg-neutral-800' : 'bg-neutral-200'}`}>
														{getPlatformIcon(log.sl_platform, "w-4 h-4")}
													</div>
												)}
												<div className="flex flex-col min-w-0">
													<div className="flex items-center gap-1.5">
														<span className={`text-[13px] font-mono font-medium truncate ${isDark ? 'text-cyan-400 group-hover:text-cyan-300' : 'text-cyan-700'}`}>
															@{username || 'UNKNOWN'}
														</span>
														{getAvatarFromLog(log) && (
															<div className="opacity-70">
																{getPlatformIcon(log.sl_platform)}
															</div>
														)}
													</div>
												</div>
											</div>

											{/* Event Type */}
											<div className="hidden md:flex items-center">
												<span className={`text-[11px] font-mono px-1.5 py-0.5 rounded-sm uppercase tracking-widest ${isCritical ? (isDark ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20' : 'bg-indigo-50 text-indigo-700 border border-indigo-200') : (isDark ? 'bg-neutral-800 text-neutral-300 border border-neutral-700' : 'bg-neutral-200 text-neutral-600 border border-neutral-300')}`}>
													{title}
												</span>
											</div>

											{/* Details */}
											<div className="flex justify-end pr-1">
												{detailRenderer || <span className={`text-[11px] font-mono ${mutedTest}`}>[LOGGED]</span>}
											</div>
										</div>
									);
								})}
							</div>
						) : (
							<p className={`text-[10px] font-mono p-4 ${mutedTest}`}>[ No intelligence streams active ]</p>
						)}
					</div>
				</div>

			</div>
		</div>
	);
};

export default Dashboard;
